import React, { useState, useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { logEvent } from 'firebase/analytics';
import { firebaseConfig } from '../firebase/config';
import { mapMarkerPalette } from '../mapMarkerPalette';
import { buildDarkMapStyle } from '../mapDarkStyle';
import { useThemeMode } from '../themeMode';
import { canChooseMapMarker } from '../utils/accountFeatures';

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
} else {
  firebase.app();
}

const db = firebase.firestore();

const userLocationIcon = {
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" fill="${mapMarkerPalette.user}" opacity="1" />
      <circle cx="12" cy="12" r="11" fill="${mapMarkerPalette.user}" opacity="0.3" stroke="${mapMarkerPalette.userRing}" stroke-width="2" />
    </svg>
  `),
  scaledSize: { width: 24, height: 24 },
};

const markerColors = mapMarkerPalette;

// You can customize these values
const AVAILABLE_OPACITY = 1;
const UNAVAILABLE_OPACITY = 0.8;  // More transparent for unavailable courts

// Add your custom SVGs here
const customSVGs = {
  default: null,  // Will use default Google Maps marker path
  tennisRacquet: {
    type: 'full',
    content: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.52,2.49c-2.34-2.34-6.62-1.87-9.55,1.06c-1.6,1.6-2.52,3.87-2.54,5.46c-0.02,1.58,0.26,3.89-1.35,5.5l-4.24,4.24l1.42,1.42l4.24-4.24c1.61-1.61,3.92-1.33,5.5-1.35s3.86-0.94,5.46-2.54C21.38,9.11,21.86,4.83,19.52,2.49z"/><path fill="${mapMarkerPalette.white}" d="M10.32,11.68c-1.53-1.53-1.05-4.61,1.06-6.72s5.18-2.59,6.72-1.06c1.53,1.53,1.05,4.61-1.06,6.72S11.86,13.21,10.32,11.68z"/><path d="M18,15c-1.02,0-2.05,0.39-2.83,1.17c-1.56,1.56-1.56,4.09,0,5.66C15.95,22.61,16.98,23,18,23s2.05-0.39,2.83-1.17c1.56-1.56,1.56-4.09,0-5.66C20.05,15.39,19.02,15,18,15z"/><path fill="${mapMarkerPalette.white}" d="M18,17c0.53,0,1.04,0.21,1.41,0.59c0.78,0.78,0.78,2.05,0,2.83C19.04,20.79,18.53,21,18,21s-1.04-0.21-1.41-0.59c-0.78-0.78-0.78-2.05,0-2.83C16.96,17.21,17.47,17,18,17z"/></svg>`
  },
  tennisBall: {
    type: 'full',
    content: `<svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24" viewBox="0 0 24 24" width="24"><g><rect fill="none" height="24" width="24"/></g><g><g><g><path d="M3.81,6.28C2.67,7.9,2,9.87,2,12s0.67,4.1,1.81,5.72C6.23,16.95,8,14.68,8,12S6.23,7.05,3.81,6.28z"/></g><g><path d="M20.19,6.28C17.77,7.05,16,9.32,16,12s1.77,4.95,4.19,5.72C21.33,16.1,22,14.13,22,12S21.33,7.9,20.19,6.28z"/></g><g><path d="M14,12c0-3.28,1.97-6.09,4.79-7.33C17.01,3.02,14.63,2,12,2S6.99,3.02,5.21,4.67C8.03,5.91,10,8.72,10,12 s-1.97,6.09-4.79,7.33C6.99,20.98,9.37,22,12,22s5.01-1.02,6.79-2.67C15.97,18.09,14,15.28,14,12z"/></g></g></g></svg>`,
    needsBackground: true
  },
  star: {
    type: 'path',
    content: "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
  },
  heart: {
    type: 'path',
    content: "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
  },
  dot: null
};

const Map = ({ isFrench, analytics, updateLastRefreshTime, autoRefreshEnabled, auth, accountType: effectiveAccountType }) => {
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 45.5017,
    lng: -73.5673,
  });
  const [refreshInterval, setRefreshInterval] = useState(5 * 60 * 1000); // Default 5 minutes
  const refreshTimeoutRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [markerIcon, setMarkerIcon] = useState('default');
  const [isMarkersReady, setIsMarkersReady] = useState(false);
  const markerPreferencesUnsubscribe = useRef(null);
  const { resolvedMode } = useThemeMode();
  // Rebuilt on mode change because the tokens it reads change with the
  // [data-theme] attribute. null in light mode leaves Google's default basemap.
  const mapStyles = React.useMemo(
    () => (resolvedMode === 'dark' ? buildDarkMapStyle() : null),
    [resolvedMode]
  );
  const [quickLinks, setQuickLinks] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Update default zoom based on screen size
  const defaultZoom = isMobile ? 11 : 12;  // Zoomed out slightly on mobile

  const formatDate = (dateStr, isFrench) => {
    // Create date object with explicit timezone handling for Eastern Time
    const [year, month, day] = dateStr.split('-').map(Number);
    // Months are 0-based in JavaScript Date
    const date = new Date(year, month - 1, day);
    
    const weekdays = {
      en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      fr: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    };
    const months = {
      en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      fr: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    };
    
    const weekday = weekdays[isFrench ? 'fr' : 'en'][date.getDay()];
    const monthName = months[isFrench ? 'fr' : 'en'][date.getMonth()];
    
    return `${weekday} ${monthName} ${day}`;
  };

  // Site addresses arrive fully qualified ("…, Montréal, Québec, H2K 2W4, Canada").
  // Inside a map popup the province, postal code and country are noise, so trim
  // them off and keep the street plus city.
  const formatSiteAddress = (address) => {
    if (!address) return '';

    const parts = address.split(',').map(part => part.trim()).filter(Boolean);
    const isNoise = (part) => (
      /^(canada|ca)$/i.test(part) ||
      /^[a-z]\d[a-z]\s*\d[a-z]\d$/i.test(part) ||
      /^(qu[ée]bec|qc|ontario|on)$/i.test(part)
    );

    while (parts.length > 2 && isNoise(parts[parts.length - 1])) {
      parts.pop();
    }

    return parts.join(', ');
  };

  const formatPhoneHref = (phone) => {
    const digits = String(phone).replace(/[^\d+]/g, '');
    return digits ? `tel:${digits}` : null;
  };

  const groupAvailabilitiesByDate = (stats) => {
    if (!stats) return {};
    
    const groupedStats = {};
    
    stats.forEach(stat => {
      // Extract just the date part from the datetime string
      const dateStr = stat.startDateTime.split('T')[0];
      
      if (!groupedStats[dateStr]) {
        groupedStats[dateStr] = [];
      }
      
      groupedStats[dateStr].push({
        ...stat,
        count: stat.count || (stat.reservationLinks ? stat.reservationLinks.length : 0)
      });
    });
    
    // Sort times within each date
    Object.keys(groupedStats).forEach(date => {
      groupedStats[date].sort((a, b) => {
        // Parse times directly from the ISO strings
        const timeA = a.startDateTime.split('T')[1].split(':').map(Number);
        const timeB = b.startDateTime.split('T')[1].split(':').map(Number);
        // Compare hours first, then minutes if hours are equal
        return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
      });
    });
    
    return groupedStats;
  };

  useEffect(() => {
    // Subscribe to quickLinks document
    const unsubscribe = firebase.firestore()
      .collection('overview')
      .doc('quickLinks')
      .onSnapshot((doc) => {
        if (doc.exists) {
          setQuickLinks(doc.data().quickLinks);
        }
      });

    return () => unsubscribe();
  }, []);

  const formatAvailability = (stat) => {
    const [hours, minutes] = stat.startDateTime.split('T')[1].split(':').map(Number);
    const formattedStart = `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
    const count = stat.count;

    return (
      <button
        type="button"
        className="map-popup-slot"
        key={stat.startDateTime}
        aria-label={isFrench
          ? `${formattedStart} — ${count} ${count > 1 ? 'terrains disponibles' : 'terrain disponible'}`
          : `${formattedStart} — ${count} ${count > 1 ? 'courts' : 'court'} available`}
        onClick={() => {
          if (quickLinks && stat.linkSearchKey) {
            const actualLink = quickLinks[stat.linkSearchKey];
            if (actualLink) {
              window.open(actualLink, "_blank");
              logEvent(analytics, 'click_availability_link', {
                link: actualLink,
                startDate: stat.startDateTime,
                startTime: formattedStart
              });
            } else {
              console.error('Link not found in quickLinks');
            }
          } else {
            console.error('QuickLinks not loaded or linkSearchKey missing');
          }
        }}
      >
        {formattedStart} ({count})
      </button>
    );
  };

  useEffect(() => {
    /*if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserPosition({ lat: latitude, lng: longitude });
          setMapCenter({ lat: latitude, lng: longitude });
          logEvent(analytics, 'get_user_position', { status: 'success', lat: latitude, lng: longitude });
        },
        (error) => {
          console.error('Error getting user position:', error);
          logEvent(analytics, 'get_user_position', { status: 'error', message: error.message });
        }
      );
    } else {
      logEvent(analytics, 'get_user_position', { status: 'error', message: 'Geolocation is not supported by this browser.' });
      console.error('Geolocation is not supported by this browser.');
    }*/
  }, []);

  useEffect(() => {
    const getMarkers = async () => {
      try {
        setIsMarkersReady(false);
        const overviewSnapshot = await db.collection('overview').doc('loadedSites').get();
        const newMarkers = [];
        for (const doc in overviewSnapshot.data()) {
          const data = overviewSnapshot.data()[doc];
          let lat, lng;

          if (data.sitePosition) {
            lat = data.sitePosition.lat;
            lng = data.sitePosition.lng;
          }

          newMarkers.push({
            id: doc,
            sitePosition: { lat, lng },
            label: data.siteName,
            siteAddress: data.siteAddress,
            sitePhone: data.sitePhone,
            stats: data.stats,
          });
        }

        setMarkers(newMarkers);
        
        // Update selected marker if it exists
        if (selectedMarker) {
          const updatedMarker = newMarkers.find(marker => 
            marker.sitePosition.lat === selectedMarker.sitePosition.lat && 
            marker.sitePosition.lng === selectedMarker.sitePosition.lng
          );
          if (updatedMarker) {
            setSelectedMarker(updatedMarker);
          }
        }

        setIsMarkersReady(true);
        
        // Get new refresh interval and update last refresh time
        const newInterval = await updateLastRefreshTime();
        setRefreshInterval(newInterval);

        // Schedule next refresh only if auto-refresh is enabled
        if (autoRefreshEnabled) {
          refreshTimeoutRef.current = setTimeout(() => {
            getMarkers();
          }, newInterval);
        }
      } catch (err) {
        console.error("Error while fetching markers: ", err);
        setIsMarkersReady(true); // Set to true even on error to prevent infinite loading
      }
    };

    getMarkers();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [updateLastRefreshTime, autoRefreshEnabled]);

  useEffect(() => {
    // Cleanup previous listener if exists
    if (markerPreferencesUnsubscribe.current) {
      markerPreferencesUnsubscribe.current();
    }

    setIsMarkersReady(false); // Reset markers when marker icon changes
    // Set up a real-time listener for marker preferences.
    const currentUser = firebase.auth().currentUser;
    if (currentUser?.uid) {
      markerPreferencesUnsubscribe.current = firebase.firestore()
        .collection('userPreferences')
        .doc(currentUser.uid)
        .onSnapshot((doc) => {
          if (doc.exists) {
            const data = doc.data();
            if (canChooseMapMarker(currentUser, effectiveAccountType, data.markerIcon)) {
              setMarkerIcon(data.markerIcon);
            } else {
              setMarkerIcon('default');
            }
          }
          setIsMarkersReady(true);
        }, (error) => {
          console.error("Error listening to marker preferences:", error);
          setIsMarkersReady(true);
        });
    } else {
      setIsMarkersReady(true);
    }

    // Cleanup listener on unmount or when auth changes
    return () => {
      if (markerPreferencesUnsubscribe.current) {
        markerPreferencesUnsubscribe.current();
      }
    };
  }, [firebase.auth().currentUser?.uid, effectiveAccountType]);

  const getMarkerIcon = (hasAvailabilities) => {
    const currentUser = firebase.auth().currentUser;
    const effectiveMarkerIcon = canChooseMapMarker(currentUser, effectiveAccountType, markerIcon)
      ? markerIcon
      : 'default';
    const opacity = hasAvailabilities ? AVAILABLE_OPACITY : UNAVAILABLE_OPACITY;
    const color = hasAvailabilities ? markerColors[effectiveMarkerIcon] : markerColors.unavailable;
    const size = 40;
    
    // Handle full SVG content
    if (customSVGs[effectiveMarkerIcon]?.type === 'full') {
      if (!customSVGs[effectiveMarkerIcon].content) return null;
      
      // Special handling for tennis ball with background
      if (customSVGs[effectiveMarkerIcon].needsBackground) {
        return {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="${mapMarkerPalette.white}"/>
              <g fill="${color}" opacity="${opacity}">
                ${customSVGs[effectiveMarkerIcon].content}
              </g>
              <circle cx="12" cy="12" r="10" fill="none" stroke="${mapMarkerPalette.tennisBallDetail}" stroke-width="0.8" opacity="${opacity}"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(size, size),
        };
      }
      
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <g fill="${color}" opacity="${opacity}">
              ${customSVGs[effectiveMarkerIcon].content}
            </g>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(size, size),
      };
    }

    // Handle path-only SVG content
    if (customSVGs[effectiveMarkerIcon]?.type === 'path') {
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path fill="${color}" opacity="${opacity}" d="${customSVGs[effectiveMarkerIcon].content}"/>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(size, size),
      };
    }

    // Default marker with custom color
    if (effectiveMarkerIcon === 'default') {
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path fill="${color}" opacity="${opacity}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(size, size),
      };
    }

    // Simple dot marker
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8" fill="${color}" opacity="${opacity}"/>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(size * 0.8, size * 0.8),
    };
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <GoogleMap
      mapContainerClassName="map-view"
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={mapCenter}
      zoom={defaultZoom}
      options={{ 
        clickableIcons: false,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
        zoomControl: !isMobile,
        styles: mapStyles,
      }}

    >
      {userPosition && (
        <Marker
          position={userPosition}
          icon={userLocationIcon}
        />
      )}
      {isMarkersReady && markers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.sitePosition}
          onClick={() => {
            setSelectedMarker(marker);
            setTab(0);
            setMapCenter(marker.sitePosition);
            logEvent(analytics, 'click_marker', { siteName: marker.label });
          }}
          opacity={marker.stats && marker.stats.length > 0 ? 1 : 0.3}
          icon={getMarkerIcon(marker.stats && marker.stats.length > 0)}
          title={!marker.stats || marker.stats.length === 0 ? (isFrench ? "Aucune disponibilité" : "No availabilities") : ""}
          zIndex={marker.stats && marker.stats.length > 0 ? 2 : 1}
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={{
            lat: selectedMarker.sitePosition.lat,
            lng: selectedMarker.sitePosition.lng,
          }}
          options={{
            pixelOffset: new window.google.maps.Size(0, -45), // Adjust -40 based on the height of the InfoWindow
          }}
          onCloseClick={() => {
            setSelectedMarker(null);
          }}
        >
          <div className="map-popup">
            <div className="map-popup-header">
              <h2 className="map-popup-title">{selectedMarker.label}</h2>
              {(selectedMarker.siteAddress || selectedMarker.sitePhone) && (
                <div className="map-popup-meta">
                  {selectedMarker.siteAddress && (
                    <a
                      className="map-popup-address"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedMarker.siteAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={selectedMarker.siteAddress}
                    >
                      {formatSiteAddress(selectedMarker.siteAddress)}
                    </a>
                  )}
                  {selectedMarker.sitePhone && (
                    formatPhoneHref(selectedMarker.sitePhone)
                      ? (
                        <a
                          className="map-popup-phone"
                          href={formatPhoneHref(selectedMarker.sitePhone)}
                        >
                          {selectedMarker.sitePhone}
                        </a>
                      )
                      : <span className="map-popup-phone">{selectedMarker.sitePhone}</span>
                  )}
                </div>
              )}
            </div>

            {selectedMarker.stats && selectedMarker.stats.length > 0 ? (
              (() => {
                const groupedStats = groupAvailabilitiesByDate(selectedMarker.stats);
                const dates = Object.keys(groupedStats).sort();
                // `tab` survives a switch between markers, so clamp it to the
                // dates this park actually has.
                const activeIndex = Math.min(tab, dates.length - 1);
                const activeDate = dates[activeIndex];
                const openCount = groupedStats[activeDate]
                  .reduce((total, stat) => total + (stat.count || 0), 0);

                return (
                  <div className="map-popup-body">
                    <div className="map-popup-section-head">
                      <span className="map-popup-eyebrow">
                        {isFrench ? "Disponibilités" : "Availabilities"}
                      </span>
                      <span className="availability-pill">
                        {isFrench ? `${openCount} libres` : `${openCount} open`}
                      </span>
                    </div>

                    <div className="map-popup-datebar">
                      <div className="map-popup-dates" role="tablist">
                        {dates.map((date, index) => (
                          <button
                            type="button"
                            key={date}
                            role="tab"
                            id={`map-popup-date-${index}`}
                            aria-selected={index === activeIndex}
                            aria-controls={`map-popup-slots-${index}`}
                            className={index === activeIndex ? 'map-popup-date is-active' : 'map-popup-date'}
                            onClick={() => setTab(index)}
                          >
                            {formatDate(date, isFrench)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div
                      className="map-popup-slots"
                      role="tabpanel"
                      id={`map-popup-slots-${activeIndex}`}
                      aria-labelledby={`map-popup-date-${activeIndex}`}
                    >
                      {groupedStats[activeDate].map(stat => formatAvailability(stat))}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="map-popup-body">
                <div className="map-popup-section-head">
                  <span className="map-popup-eyebrow">
                    {isFrench ? "Disponibilités" : "Availabilities"}
                  </span>
                  <span className="availability-pill is-empty">
                    {isFrench ? "Aucun créneau" : "No open slots"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default Map;
